import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import VideoLibraryRoundedIcon from "@mui/icons-material/VideoLibraryRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import HowToVoteRoundedIcon from "@mui/icons-material/HowToVoteRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import SortRoundedIcon from "@mui/icons-material/SortRounded";
import { CircularProgress } from "@mui/material";
import { getAllShorts } from "../../../api/shortsVideo";
import { getToken, getUserId } from "../../../api/index";
import "./AttachmentPickers.css";

const SORT_OPTIONS = [
  { value: "newest", label: "Newest First", icon: "🕒" },
  { value: "oldest", label: "Oldest First", icon: "⏳" },
  { value: "title_asc", label: "Title (A – Z)", icon: "🔤" },
  { value: "title_desc", label: "Title (Z – A)", icon: "🔤" },
];

export const getShortTitle = (short) => {
  if (!short) return "Short Video";
  if (short.title && typeof short.title === "string" && short.title.trim()) {
    return short.title.trim();
  }
  if (short.description && typeof short.description === "string" && short.description.trim()) {
    const d = short.description.trim();
    return d.length > 50 ? `${d.substring(0, 47)}...` : d;
  }
  if (short.voteResponse?.title && typeof short.voteResponse.title === "string" && short.voteResponse.title.trim()) {
    return short.voteResponse.title.trim();
  }
  if (short.name && typeof short.name === "string" && short.name.trim()) {
    return short.name.trim();
  }
  if (short.caption && typeof short.caption === "string" && short.caption.trim()) {
    return short.caption.trim();
  }
  if (short.videoUrl) {
    try {
      const urlParts = short.videoUrl.split("?")[0].split("/");
      const rawFileName = decodeURIComponent(urlParts[urlParts.length - 1]);
      const cleanName = rawFileName
        .replace(/^[0-9a-fA-F-]{36}_/, "")
        .replace(/\.[^/.]+$/, "")
        .replace(/[-_]/g, " ")
        .trim();
      if (cleanName && cleanName.length > 1) {
        return cleanName.charAt(0).toUpperCase() + cleanName.slice(1);
      }
    } catch (_) {}
  }
  return "Short Video";
};

export default function ShortsPickerModal({ open, onClose, onSelectShort, selectedShortId }) {
  const [shorts, setShorts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("all"); // 'all' | 'my'
  const [sortBy, setSortBy] = useState("newest"); // 'newest' | 'oldest' | 'title_asc' | 'title_desc'
  const [sortOpen, setSortOpen] = useState(false);
  const [selectedShort, setSelectedShort] = useState(null);
  const [playingShortId, setPlayingShortId] = useState(null);

  const sortRef = useRef(null);
  const currentUserId = getUserId();
  const token = getToken();

  // Close sort menu on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortRef.current && !sortRef.current.contains(event.target)) {
        setSortOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (open) {
      fetchShorts();
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prevOverflow;
      };
    } else {
      setPlayingShortId(null);
    }
  }, [open]);

  // Sync selectedShort with selectedShortId when shorts load or modal opens
  useEffect(() => {
    if (selectedShortId && shorts.length > 0) {
      const match = shorts.find((s) => String(s.videoId || s.id) === String(selectedShortId));
      if (match) setSelectedShort(match);
    } else if (!selectedShortId) {
      setSelectedShort(null);
    }
  }, [selectedShortId, shorts, open]);

  const fetchShorts = async () => {
    try {
      setLoading(true);
      const params = {
        UserId: currentUserId || undefined,
        skip: 0,
        take: 50,
      };
      const res = await getAllShorts(params);
      const data = res?.data || res || [];
      if (Array.isArray(data)) {
        setShorts(data);
      } else {
        setShorts([]);
      }
    } catch (err) {
      console.error("Error loading shorts for picker:", err);
      setShorts([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredShorts = useMemo(() => {
    const list = shorts.filter((s) => {
      const displayTitle = getShortTitle(s).toLowerCase();
      const term = searchTerm.toLowerCase().trim();

      const matchesSearch = !term || displayTitle.includes(term);

      if (!matchesSearch) return false;

      if (activeFilter === "my") {
        if (!currentUserId) return false;
        const creatorId = s.createdByUser || s.userId || s.user?.userId || "";
        return String(creatorId).toLowerCase() === String(currentUserId).toLowerCase();
      }

      return true;
    });

    return list.sort((a, b) => {
      const getDate = (item) => {
        const d = item.createdAt || item.createdDate || item.createdOn || item.creationDate || 0;
        return new Date(d).getTime() || 0;
      };

      if (sortBy === "newest") {
        return getDate(b) - getDate(a);
      }
      if (sortBy === "oldest") {
        return getDate(a) - getDate(b);
      }
      if (sortBy === "title_asc") {
        return (getShortTitle(a) || "").localeCompare(getShortTitle(b) || "");
      }
      if (sortBy === "title_desc") {
        return (getShortTitle(b) || "").localeCompare(getShortTitle(a) || "");
      }
      return 0;
    });
  }, [shorts, searchTerm, activeFilter, sortBy, currentUserId]);

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return null;
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const handleCardClick = (short) => {
    setSelectedShort(short);
  };

  const handleTogglePlay = (e, short) => {
    e.stopPropagation();
    const sId = short.videoId || short.id;
    if (playingShortId === sId) {
      setPlayingShortId(null);
    } else {
      setPlayingShortId(sId);
    }
  };

  const handleConfirmSelection = () => {
    if (selectedShort) {
      setPlayingShortId(null);
      onSelectShort(selectedShort);
      onClose();
    }
  };

  if (!open) return null;

  return createPortal(
    <AnimatePresence>
      <div className="picker-modal-overlay" onClick={onClose}>
        <motion.div
          className="picker-modal-container shorts-picker-container"
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="picker-modal-header">
            <div className="picker-header-title-box">
              <div className="picker-icon-badge shorts-badge">
                <VideoLibraryRoundedIcon style={{ fontSize: 22, color: "#2dd4bf" }} />
              </div>
              <div>
                <h3 className="picker-title">Attach a Shorts</h3>
                <p className="picker-subtitle">Select a shorts to embed alongside your discussion</p>
              </div>
            </div>
            <button className="btn-picker-close" onClick={onClose} title="Close">
              <CloseIcon style={{ fontSize: 20 }} />
            </button>
          </div>

          {/* Search, Filter Tabs & Sort Controls */}
          <div className="picker-controls-row">
            <div className="picker-search-wrapper">
              <SearchIcon className="picker-search-icon" />
              <input
                type="text"
                placeholder="Search shorts by title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="picker-search-input"
                autoFocus
              />
              {searchTerm && (
                <button className="picker-search-clear" onClick={() => setSearchTerm("")}>
                  <CloseIcon style={{ fontSize: 14 }} />
                </button>
              )}
            </div>

            <div className="picker-filter-tabs">
              <button
                className={`picker-tab ${activeFilter === "all" ? "active" : ""}`}
                onClick={() => {
                  setActiveFilter("all");
                  setPlayingShortId(null);
                }}
              >
                <PublicRoundedIcon style={{ fontSize: 15 }} /> All Shorts
              </button>
              <button
                className={`picker-tab ${activeFilter === "my" ? "active" : ""}`}
                onClick={() => {
                  setActiveFilter("my");
                  setPlayingShortId(null);
                }}
              >
                <PersonOutlineRoundedIcon style={{ fontSize: 15 }} /> My Shorts
              </button>
            </div>

            {/* Custom Styled Sort Dropdown */}
            <div className="picker-sort-container" ref={sortRef}>
              <button
                type="button"
                className={`picker-sort-trigger ${sortOpen ? "open" : ""}`}
                onClick={() => setSortOpen((prev) => !prev)}
                title="Sort shorts"
              >
                <div className="picker-sort-trigger-left">
                  <SortRoundedIcon className="picker-sort-icon" />
                  <span className="picker-sort-label">
                    {SORT_OPTIONS.find((s) => s.value === sortBy)?.label || "Sort"}
                  </span>
                </div>
                <KeyboardArrowDownRoundedIcon className={`picker-sort-arrow ${sortOpen ? "rotated" : ""}`} />
              </button>

              <AnimatePresence>
                {sortOpen && (
                  <motion.div
                    className="picker-sort-menu"
                    initial={{ opacity: 0, y: -6, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.98 }}
                    transition={{ duration: 0.15, ease: "easeOut" }}
                  >
                    <div className="picker-sort-menu-header">
                      <span>Sort Shorts By</span>
                    </div>
                    <div className="picker-sort-menu-list">
                      {SORT_OPTIONS.map((opt) => {
                        const isSelected = sortBy === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            className={`picker-sort-item ${isSelected ? "selected" : ""}`}
                            onClick={() => {
                              setSortBy(opt.value);
                              setSortOpen(false);
                            }}
                          >
                            <div className="picker-sort-item-left">
                              <span className="picker-sort-item-icon">{opt.icon}</span>
                              <span className="picker-sort-item-text">{opt.label}</span>
                            </div>
                            {isSelected && (
                              <CheckRoundedIcon className="picker-sort-check" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Grid Area */}
          <div className="picker-shorts-grid">
            {loading ? (
              <div className="picker-loading-state grid-span-all">
                <CircularProgress size={32} style={{ color: "#2dd4bf" }} />
                <p>Loading shorts library...</p>
              </div>
            ) : filteredShorts.length === 0 ? (
              <div className="picker-empty-state grid-span-all">
                <VideoLibraryRoundedIcon style={{ fontSize: 44, opacity: 0.3, color: "#2dd4bf" }} />
                <h4>No shorts videos found</h4>
                <p>{searchTerm ? "Try refining your search keyword." : "No shorts available to attach."}</p>
              </div>
            ) : (
              filteredShorts.map((short) => {
                const sId = short.videoId || short.id;
                const isSelected = selectedShort && String(selectedShort.videoId || selectedShort.id) === String(sId);
                const isPlaying = playingShortId === sId;
                const durationFormatted = formatDuration(short.duration);
                const creatorName = short.createdByUserName || short.user?.username || short.authorName || "Creator";
                const displayTitle = getShortTitle(short);

                return (
                  <div
                    key={sId}
                    className={`picker-short-card ${isSelected ? "selected" : ""} ${isPlaying ? "playing" : ""}`}
                    onClick={() => handleCardClick(short)}
                    onDoubleClick={() => {
                      setPlayingShortId(null);
                      onSelectShort(short);
                      onClose();
                    }}
                  >
                    {/* Media Thumbnail & Video Player */}
                    <div className="picker-short-media">
                      {short.videoUrl ? (
                        <video
                          ref={(el) => {
                            if (el) {
                              if (isPlaying) {
                                el.play().catch(() => {});
                              } else {
                                el.pause();
                              }
                            }
                          }}
                          src={short.videoUrl}
                          poster={short.thumbnailUrl || undefined}
                          className="picker-short-img"
                          preload="metadata"
                          playsInline
                          loop
                          onEnded={() => setPlayingShortId(null)}
                        />
                      ) : short.thumbnailUrl ? (
                        <img
                          src={short.thumbnailUrl}
                          alt={displayTitle}
                          className="picker-short-img"
                        />
                      ) : (
                        <div className="picker-short-placeholder">
                          <VideoLibraryRoundedIcon style={{ fontSize: 36, opacity: 0.4 }} />
                          <span style={{ fontSize: "0.72rem", opacity: 0.6 }}>Short Video</span>
                        </div>
                      )}

                      {/* Interactive Play / Pause Button */}
                      {short.videoUrl && (
                        <button
                          type="button"
                          className={`picker-short-play-btn ${isPlaying ? "playing" : ""}`}
                          onClick={(e) => handleTogglePlay(e, short)}
                          title={isPlaying ? "Pause preview" : "Play preview"}
                        >
                          {isPlaying ? (
                            <PauseRoundedIcon style={{ fontSize: 26, color: "#2dd4bf" }} />
                          ) : (
                            <PlayArrowRoundedIcon style={{ fontSize: 28, color: "#fff", marginLeft: 2 }} />
                          )}
                        </button>
                      )}

                      {durationFormatted && (
                        <span className="picker-short-duration">
                          <AccessTimeRoundedIcon style={{ fontSize: 11 }} /> {durationFormatted}
                        </span>
                      )}

                      {short.voteId && (
                        <span className="picker-short-linked-vote" title="Contains linked interactive ballot">
                          <HowToVoteRoundedIcon style={{ fontSize: 13 }} />
                          <span>Linked Ballot</span>
                        </span>
                      )}

                      <div className="picker-short-select-indicator">
                        {isSelected ? (
                          <CheckCircleRoundedIcon style={{ color: "#2dd4bf", fontSize: 24 }} />
                        ) : (
                          <RadioButtonUncheckedRoundedIcon style={{ color: "rgba(255,255,255,0.7)", fontSize: 24 }} />
                        )}
                      </div>
                    </div>

                    {/* Meta info */}
                    <div className="picker-short-info">
                      <div className="picker-short-text-group">
                        <h4 className="picker-short-title" title={displayTitle}>{displayTitle}</h4>
                        <p className="picker-short-desc" title={short.description || ""}>
                          {short.description && short.description !== displayTitle ? short.description : "\u00A0"}
                        </p>
                      </div>
                      <div className="picker-short-author">
                        <span className="picker-short-creator-name">
                          {short.createdByUserPhoto ? (
                            <img
                              src={short.createdByUserPhoto}
                              alt={creatorName}
                              className="picker-short-author-avatar"
                              onError={(e) => { e.target.style.display = "none"; }}
                            />
                          ) : (
                            <span style={{ fontSize: 11 }}>👤</span>
                          )}
                          <span>{creatorName}</span>
                        </span>
                        {short.createdAt && (
                          <span className="picker-short-date">
                            {new Date(short.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Sticky Footer Toolbar */}
          <div className="picker-modal-footer">
            <div className="picker-footer-selection-info">
              {selectedShort ? (
                <div className="picker-selection-tag short-tag">
                  <VideoLibraryRoundedIcon style={{ fontSize: 16 }} />
                  <span className="picker-selection-title">{getShortTitle(selectedShort)}</span>
                  <button
                    type="button"
                    className="btn-picker-clear-sel"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedShort(null);
                    }}
                    title="Deselect"
                  >
                    <CloseIcon style={{ fontSize: 14 }} />
                  </button>
                </div>
              ) : (
                <span className="picker-none-selected">Select a video above to attach</span>
              )}
            </div>

            <div className="picker-footer-actions">
              <button type="button" className="btn-picker-cancel" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-picker-confirm"
                disabled={!selectedShort}
                onClick={handleConfirmSelection}
              >
                <CheckCircleRoundedIcon style={{ fontSize: 17 }} />
                Attach Short
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
}
